if (!customElements.get('wls-blog-all')) {
    class WLSBlogAll extends HTMLElement {
        constructor() {
            super();

            this.selectors = {
                pill: '.js-wls-blog-all-pill',
                card: '.js-wls-blog-all-card',
                spec: '.js-wls-blog-all-spec',
                empty: '.js-wls-blog-all-empty',
                pagination: '.js-wls-blog-all-pagination',
                iconPrev: '.js-wls-blog-all-icon-prev',
                iconNext: '.js-wls-blog-all-icon-next',
                heroInput: '.js-wls-blog-hero-search-input'
            };

            this.activeTag = '';
            this.query = '';
            this.currentPage = 1;

            this.onPillClick = this.onPillClick.bind(this);
            this.onSearchInput = this.onSearchInput.bind(this);
            this.onPaginationClick = this.onPaginationClick.bind(this);
        }

        connectedCallback() {
            this.perPage = parseInt(this.dataset.perPage, 10) || 9;

            this.pills = Array.from(this.querySelectorAll(this.selectors.pill));
            this.cards = Array.from(this.querySelectorAll(this.selectors.card));
            this.spec = this.querySelector(this.selectors.spec);
            this.empty = this.querySelector(this.selectors.empty);
            this.pagination = this.querySelector(this.selectors.pagination);

            // Arrow icons live in the markup (inert <template>s), read once here.
            const prevIcon = this.querySelector(this.selectors.iconPrev);
            const nextIcon = this.querySelector(this.selectors.iconNext);
            this.icons = {
                prev: prevIcon ? prevIcon.innerHTML : '',
                next: nextIcon ? nextIcon.innerHTML : ''
            };

            // The hero search field is a separate section above this one.
            this.heroInput = document.querySelector(this.selectors.heroInput);

            this.pills.forEach((pill) => pill.addEventListener('click', this.onPillClick));

            if (this.heroInput) {
                this.query = this.heroInput.value.trim().toLowerCase();
                this.heroInput.addEventListener('input', this.onSearchInput);
            }

            if (this.pagination) {
                this.pagination.addEventListener('click', this.onPaginationClick);
            }

            this.update();
        }

        disconnectedCallback() {
            this.pills.forEach((pill) => pill.removeEventListener('click', this.onPillClick));
            if (this.heroInput) this.heroInput.removeEventListener('input', this.onSearchInput);
            if (this.pagination) this.pagination.removeEventListener('click', this.onPaginationClick);
        }

        onPillClick(event) {
            const pill = event.currentTarget;
            this.activeTag = pill.dataset.filter || '';

            this.pills.forEach((item) => item.classList.toggle('is-active', item === pill));

            // A new filter always starts back at the first page.
            this.currentPage = 1;
            this.update();
        }

        onSearchInput(event) {
            this.query = event.target.value.trim().toLowerCase();
            this.currentPage = 1;
            this.update();
        }

        onPaginationClick(event) {
            const control = event.target.closest('[data-page]');
            if (!control || control.disabled) return;

            const page = parseInt(control.dataset.page, 10);
            if (!page || page === this.currentPage) return;

            this.currentPage = page;
            this.update();
            this.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }

        matches(card) {
            const tags = (card.dataset.tags || '').split(' ');
            const tagMatch = this.activeTag === '' || tags.indexOf(this.activeTag) !== -1;
            const searchMatch = this.query === '' || (card.dataset.search || '').indexOf(this.query) !== -1;
            return tagMatch && searchMatch;
        }

        update() {
            // Everything that matches the active tag + search, in document order.
            const matched = this.cards.filter((card) => this.matches(card));
            const total = matched.length;
            const filtering = this.activeTag !== '' || this.query !== '';

            // On the default (unfiltered) view the newsletter card takes one of the
            // grid slots on page one, so page one holds one fewer article card.
            const specCounts = Boolean(this.spec) && !filtering;
            const firstPageArticles = specCounts ? this.perPage - 1 : this.perPage;

            let totalPages;
            if (total <= firstPageArticles) {
                totalPages = 1;
            } else {
                totalPages = 1 + Math.ceil((total - firstPageArticles) / this.perPage);
            }

            // Keep the page within range after a filter shrinks the result set.
            if (this.currentPage > totalPages) this.currentPage = totalPages;
            if (this.currentPage < 1) this.currentPage = 1;

            // Article slice for the current page (page one is shorter when the spec counts).
            let start;
            let end;
            if (this.currentPage === 1) {
                start = 0;
                end = firstPageArticles;
            } else {
                start = firstPageArticles + (this.currentPage - 2) * this.perPage;
                end = start + this.perPage;
            }

            const visible = matched.slice(start, end);
            this.cards.forEach((card) => { card.hidden = true; });
            visible.forEach((card) => { card.hidden = false; });

            // The newsletter card only belongs on the default, unfiltered first page.
            if (this.spec) {
                this.spec.hidden = !(specCounts && this.currentPage === 1);
            }

            if (this.empty) {
                this.empty.hidden = total !== 0;
            }

            this.renderPagination(totalPages);
        }

        renderPagination(totalPages) {
            if (!this.pagination) return;

            if (totalPages <= 1) {
                this.pagination.hidden = true;
                this.pagination.innerHTML = '';
                return;
            }

            const current = this.currentPage;
            let html = '';

            const prevDisabled = current === 1;
            html += `<button type="button" class="wls-blog-all__pagination-arrow wls-blog-all__pagination-arrow--prev"`
                + `${prevDisabled ? ' disabled' : ` data-page="${current - 1}"`} aria-label="Previous page">${this.icons.prev}</button>`;

            this.getPageParts(totalPages).forEach((page) => {
                if (page === '...') {
                    html += '<span class="wls-blog-all__pagination-dots">…</span>';
                } else if (page === current) {
                    html += `<span class="wls-blog-all__pagination-page is-active" aria-current="page">${page}</span>`;
                } else {
                    html += `<button type="button" class="wls-blog-all__pagination-page" data-page="${page}">${page}</button>`;
                }
            });

            const nextDisabled = current === totalPages;
            html += `<button type="button" class="wls-blog-all__pagination-arrow wls-blog-all__pagination-arrow--next"`
                + `${nextDisabled ? ' disabled' : ` data-page="${current + 1}"`} aria-label="Next page">${this.icons.next}</button>`;

            this.pagination.innerHTML = html;
            this.pagination.hidden = false;
        }

        // Always show the first/last page plus a window around the current one,
        // inserting "..." wherever pages are skipped.
        getPageParts(totalPages) {
            const current = this.currentPage;
            const delta = 1;
            const range = [];
            const parts = [];

            for (let i = 1; i <= totalPages; i += 1) {
                if (i === 1 || i === totalPages || (i >= current - delta && i <= current + delta)) {
                    range.push(i);
                }
            }

            let previous = 0;
            range.forEach((page) => {
                if (page - previous > 1) parts.push('...');
                parts.push(page);
                previous = page;
            });

            return parts;
        }
    }

    customElements.define('wls-blog-all', WLSBlogAll);
}
